import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { useSubmit } from '../../hooks/useSubmit'
import { exportSales, exportStockMovements, fetchSalesReport } from '../adminApi'
import { downloadCsv } from '../../utils/download'
import { usePeriodParams } from './usePeriodParams'
import type { RequestOptions } from '../../core/types/backend'
import type { SalesChannel } from '../../core/types/adminRequests'

/**
 * Het omzetscherm: periode in het adres (?periode=&van=&tot=), het rapport van de server
 * (die rekent; het scherm telt niets op) en de CSV-export voor de boekhouder.
 */
export function useSales() {
  const [params, setParams] = useSearchParams()
  const periodState = usePeriodParams()
  const { from, to, groupBy } = periodState

  const asked = params.get('kanaal')
  const channel: SalesChannel | undefined = asked === 'webshop' || asked === 'salon' ? asked : undefined

  const load = useCallback((opts: RequestOptions) => fetchSalesReport(from, to, groupBy, channel, opts), [from, to, groupBy, channel])
  const { data: report, status, reload } = useLatestLoad(load)

  const setChannel = useCallback(
    (next: SalesChannel | 'alle') =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (next === 'alle') p.delete('kanaal')
          else p.set('kanaal', next)
          return p
        },
        { replace: true },
      ),
    [setParams],
  )

  const download = useSubmit(async (kind: 'omzet' | 'voorraad') => {
    const csv = kind === 'omzet' ? await exportSales(from, to) : await exportStockMovements(from, to)
    downloadCsv(kind === 'omzet' ? `omzet-${from}-tot-${to}.csv` : `voorraadmutaties-${from}-tot-${to}.csv`, csv)
    return true
  })

  const exportCsv = async (kind: 'omzet' | 'voorraad' = 'omzet') => {
    if (await download.submit(kind)) {
      toast.success('Export gedownload', {
        description: kind === 'omzet' ? 'Eén regel per dag en kanaal, met btw per tarief en per betaalmethode.' : 'Elke voorraadmutatie in de periode, met inkoopwaarde.',
      })
    } else toast.error('Exporteren lukte niet', { description: 'Controleer je verbinding en probeer het opnieuw.' })
  }

  return { ...periodState, channel, setChannel, report, status, reload, exportCsv, exporting: download.submitting }
}
